import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Eye,
  EyeOff,
  Save,
  X,
  Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

interface Guide {
  id: string;
  slug: string;
  title: string;
  short_description: string | null;
}

interface GuideCategory {
  id: string;
  guide_id: string;
  slug: string;
  name: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
}

export function GuideCategoriesPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [expandedGuides, setExpandedGuides] = useState<Set<string>>(new Set());
  const [editingCategory, setEditingCategory] = useState<GuideCategory | null>(null);
  const [creatingForGuide, setCreatingForGuide] = useState<string | null>(null);
  const [editingGuideId, setEditingGuideId] = useState<string | null>(null);
  const [guideTitle, setGuideTitle] = useState('');
  const [guideDescription, setGuideDescription] = useState('');
  const [newGuideTitle, setNewGuideTitle] = useState('');
  const [newGuideDescription, setNewGuideDescription] = useState('');

  const { data: guides, isLoading: guidesLoading } = useQuery({
    queryKey: ['admin-guides', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guides')
        .select('id, slug, title, short_description')
        .eq('locale', contentLocale)
        .order('order_index');
      if (error) throw error;
      return data as Guide[];
    },
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['admin-guide-categories', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guide_categories')
        .select('*')
        .eq('locale', contentLocale)
        .order('order_index');
      if (error) throw error;
      return data as GuideCategory[];
    },
  });

  const createGuide = useMutation({
    mutationFn: async () => {
      const slug = slugify(newGuideTitle);
      const { error } = await (supabase.from('guides') as any).insert({
        title: newGuideTitle,
        short_description: newGuideDescription || null,
        slug,
        icon: 'FileText',
        is_active: true,
        is_premium: false,
        order_index: (guides?.length || 0) + 1,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guides'] });
      setNewGuideTitle('');
      setNewGuideDescription('');
      toast.success('Antrag erstellt');
    },
    onError: (error: Error) => toast.error(`Fehler: ${error.message}`),
  });

  const updateGuide = useMutation({
    mutationFn: async () => {
      if (!editingGuideId) return;
      const payload: Record<string, unknown> = {
        title: guideTitle,
        short_description: guideDescription || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await (supabase.from('guides') as any)
        .update(payload)
        .eq('id', editingGuideId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guides'] });
      setEditingGuideId(null);
      toast.success('Antrag aktualisiert');
    },
    onError: (error: Error) => toast.error(`Fehler: ${error.message}`),
  });

  const createCategory = useMutation({
    mutationFn: async (data: Partial<GuideCategory>) => {
      const existingCats = categories?.filter((c) => c.guide_id === data.guide_id) || [];
      const { error } = await (supabase.from('guide_categories') as any).insert({
        guide_id: data.guide_id,
        name: data.name,
        slug: data.slug,
        order_index: existingCats.length + 1,
        is_active: true,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-categories'] });
      setCreatingForGuide(null);
      toast.success('Kategorie erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const updateCategory = useMutation({
    mutationFn: async (data: GuideCategory) => {
      const { error } = await (supabase.from('guide_categories') as any)
        .update({
          name: data.name,
          slug: data.slug,
          is_active: data.is_active,
        })
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-categories'] });
      setEditingCategory(null);
      toast.success('Kategorie aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('guide_categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-categories'] });
      toast.success('Kategorie gelöscht');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase.from('guide_categories') as any)
        .update({ is_active })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-categories'] });
    },
  });

  const toggleExpanded = (id: string) => {
    const newSet = new Set(expandedGuides);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setExpandedGuides(newSet);
  };

  const getCategoriesForGuide = (guideId: string) => {
    return categories?.filter((c) => c.guide_id === guideId) || [];
  };

  const isLoading = guidesLoading || categoriesLoading;

  if (isLoading) {
    return <div className="bg-white rounded-lg shadow-sm p-8">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Guide-Kategorien</h1>
            <p className="text-slate-500 mt-1">Anträge und Kategorien pro Sprache verwalten</p>
          </div>
          <LocaleSelector />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        <div className="p-4 bg-slate-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              placeholder="Neuer Antrag: Name"
              value={newGuideTitle}
              onChange={(e) => setNewGuideTitle(e.target.value)}
            />
            <input
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              placeholder="Beschreibung"
              value={newGuideDescription}
              onChange={(e) => setNewGuideDescription(e.target.value)}
            />
          </div>
          <div className="mt-2">
            <button
              disabled={!newGuideTitle.trim() || createGuide.isPending}
              onClick={() => createGuide.mutate()}
              className="px-3 py-2 text-sm bg-teal-600 text-white rounded-lg disabled:opacity-50"
            >
              Hinzufügen
            </button>
          </div>
        </div>

        {guides?.map((guide) => {
          const guideCategories = getCategoriesForGuide(guide.id);
          const isExpanded = expandedGuides.has(guide.id);
          const isEditingGuide = editingGuideId === guide.id;

          return (
            <div key={guide.id}>
              <div className="flex items-center gap-4 p-4 hover:bg-slate-50">
                <button onClick={() => toggleExpanded(guide.id)} className="text-slate-400 hover:text-slate-600">
                  {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{guide.title}</span>
                    <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{guideCategories.length} Kategorien</span>
                  </div>
                  {guide.short_description && <p className="text-xs text-slate-500 mt-1">{guide.short_description}</p>}
                </div>

                <button
                  onClick={() => {
                    setEditingGuideId(guide.id);
                    setGuideTitle(guide.title);
                    setGuideDescription(guide.short_description || '');
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-lg"
                >
                  <Edit2 className="w-4 h-4" />
                  Bearbeiten
                </button>
              </div>

              {isEditingGuide && (
                <div className="px-14 pb-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input className="px-3 py-2 border border-slate-200 rounded-lg text-sm" value={guideTitle} onChange={(e) => setGuideTitle(e.target.value)} />
                    <input className="px-3 py-2 border border-slate-200 rounded-lg text-sm" value={guideDescription} onChange={(e) => setGuideDescription(e.target.value)} placeholder="Beschreibung" />
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => updateGuide.mutate()} className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg">Speichern</button>
                    <button onClick={() => setEditingGuideId(null)} className="px-3 py-1.5 text-sm text-slate-600">Abbrechen</button>
                  </div>
                </div>
              )}

              {isExpanded && (
                <div className="bg-slate-50 border-t border-slate-100">
                  <div className="py-2 px-14">
                    <button
                      onClick={() => setCreatingForGuide(guide.id)}
                      className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded text-teal-700 hover:bg-teal-50"
                    >
                      <Plus className="w-3.5 h-3.5" /> Kategorie hinzufügen
                    </button>
                  </div>
                  {guideCategories.map((category) => {
                    if (editingCategory?.id === category.id) {
                      return (
                        <CategoryForm
                          key={category.id}
                          category={editingCategory}
                          onSubmit={(data) => updateCategory.mutate({ ...editingCategory, ...data })}
                          onCancel={() => setEditingCategory(null)}
                          isLoading={updateCategory.isPending}
                          isEditing
                        />
                      );
                    }

                    return (
                      <div key={category.id} className="flex items-center gap-4 py-3 px-4 pl-14 hover:bg-slate-100">
                        <button className="text-slate-400 cursor-grab"><GripVertical className="w-4 h-4" /></button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium text-sm ${!category.is_active && 'text-slate-400'}`}>{category.name}</span>
                            <span className="text-xs text-slate-400">/{category.slug}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => toggleActive.mutate({ id: category.id, is_active: !category.is_active })} className="p-1.5 rounded">
                            {category.is_active ? <Eye className="w-3.5 h-3.5 text-green-600" /> : <EyeOff className="w-3.5 h-3.5 text-slate-400" />}
                          </button>
                          <button onClick={() => setEditingCategory(category)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteCategory.mutate(category.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}

                  {creatingForGuide === guide.id && (
                    <CategoryForm
                      guideId={guide.id}
                      onSubmit={(data) => createCategory.mutate({ ...data, guide_id: guide.id })}
                      onCancel={() => setCreatingForGuide(null)}
                      isLoading={createCategory.isPending}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface CategoryFormProps {
  category?: GuideCategory;
  guideId?: string;
  onSubmit: (data: Partial<GuideCategory>) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing?: boolean;
}

function slugify(name: string) {
  return name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function CategoryForm({ category, guideId, onSubmit, onCancel, isLoading, isEditing }: CategoryFormProps) {
  const [name, setName] = useState(category?.name || '');
  const [slug, setSlug] = useState(category?.slug || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, slug, guide_id: guideId });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-teal-50/50 py-3 px-4 pl-14 border-y border-teal-100/50">
      <div className="flex gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!isEditing) setSlug(slugify(e.target.value));
            }}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            placeholder="Name der Kategorie"
          />
        </div>
        <div className="w-48">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            placeholder="Slug"
          />
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="px-2 py-2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          <button type="submit" disabled={isLoading} className="px-3 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"><Save className="w-4 h-4" /></button>
        </div>
      </div>
    </form>
  );
}
